import React, { useEffect, useState } from 'react';
import { HelpCircle, Check, X, Trash2, Pencil, Plus } from 'lucide-react';
import { useBotQuestions } from '../hooks/useBotQuestions';
import { MAX_ANSWER, MAX_QUESTION, ago, checkAnswer, checkKnowledge, customerLabel, deliveryInfo, notifyInfo, questionCode } from '../utils/botQuestions';

const input = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading';
const card = 'bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 space-y-3';

function OpenQuestion({ q, onAnswer, onDiscard }) {
  const [text, setText] = useState('');
  const [learn, setLearn] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const notify = notifyInfo(q);
  const submit = async (e) => {
    e.preventDefault();
    const problem = checkAnswer(text);
    if (problem) { setError(problem); return; }
    setError('');
    setSaving(true);
    const ok = await onAnswer(q.id, text, learn);
    setSaving(false);
    if (ok) setText('');
  };
  return (
    <form onSubmit={submit} aria-label={`Pregunta ${questionCode(q)}`} className="border border-seryi-bg-light rounded-2xl p-4 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-seryi-text">
        <span><strong className="text-fuchsia-300">{questionCode(q)}</strong> · {customerLabel(q)} · {ago(q.created_at)}</span>
        <span className={notify.tone}>{notify.label}</span>
      </div>
      <p className="text-sm font-bold text-seryi-heading">«{q.question}»</p>
      <label htmlFor={`bq-answer-${q.id}`} className="sr-only">Respuesta para {questionCode(q)}</label>
      <textarea id={`bq-answer-${q.id}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={MAX_ANSWER} rows={2} placeholder="Tu respuesta (le llega al cliente por WhatsApp)" className={input} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-[11px] text-seryi-text">
          <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} />
          Guardarla para que el bot la responda solo la próxima vez
        </label>
        <div className="flex gap-2">
          <button type="button" disabled={saving} onClick={() => onDiscard(q.id)} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1 disabled:opacity-50"><X size={13} /> Descartar</button>
          <button type="submit" disabled={saving} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl flex items-center gap-1 disabled:opacity-50"><Check size={13} /> Responder</button>
        </div>
      </div>
      {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
    </form>
  );
}

function KnowledgeRow({ k, onUpdate, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({ question: k.question, answer: k.answer });
  const [error, setError] = useState('');
  useEffect(() => { setValues({ question: k.question, answer: k.answer }); }, [k.question, k.answer]);
  const save = async (e) => {
    e.preventDefault();
    const problem = checkKnowledge(values);
    if (problem) { setError(problem); return; }
    setError('');
    if (await onUpdate(k.id, values)) setEditing(false);
  };
  if (editing) {
    return (
      <form onSubmit={save} aria-label="Editar conocimiento" className="border border-seryi-bg-light rounded-2xl p-3 space-y-2">
        <input aria-label="Pregunta" value={values.question} maxLength={MAX_QUESTION} onChange={(e) => setValues({ ...values, question: e.target.value })} className={input} />
        <textarea aria-label="Respuesta" value={values.answer} maxLength={MAX_ANSWER} rows={2} onChange={(e) => setValues({ ...values, answer: e.target.value })} className={input} />
        <div className="flex gap-2">
          <button type="submit" className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-3 py-1.5 rounded-xl">Guardar</button>
          <button type="button" onClick={() => { setEditing(false); setError(''); setValues({ question: k.question, answer: k.answer }); }} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-1.5 rounded-xl">Cancelar</button>
        </div>
        {error && <p role="alert" className="text-xs text-rose-300">{error}</p>}
      </form>
    );
  }
  return (
    <div className={`border border-seryi-bg-light rounded-2xl p-3 space-y-1 ${k.active ? '' : 'opacity-50'}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-bold text-seryi-heading">{k.question}</p>
        <div className="flex items-center gap-1 shrink-0">
          <label className="flex items-center gap-1 text-[10px] text-seryi-text">
            <input type="checkbox" checked={k.active} onChange={(e) => onUpdate(k.id, { active: e.target.checked })} aria-label={`Activa: ${k.question}`} /> Activa
          </label>
          <button type="button" aria-label={`Editar: ${k.question}`} onClick={() => setEditing(true)} className="p-1.5 text-seryi-text hover:text-seryi-heading"><Pencil size={13} /></button>
          <button type="button" aria-label={`Borrar: ${k.question}`} onClick={() => { if (window.confirm('¿Borrar esta respuesta? El bot dejará de saberla.')) onRemove(k.id); }} className="p-1.5 text-seryi-text hover:text-rose-300"><Trash2 size={13} /></button>
        </div>
      </div>
      <p className="text-xs text-seryi-text whitespace-pre-line">{k.answer}</p>
      <p className="text-[10px] text-seryi-text">{k.source === 'dueno' ? 'Respondida a un cliente' : 'Escrita a mano'} · {ago(k.updated_at)}</p>
    </div>
  );
}

export default function BotQuestionsPage() {
  const { questions, knowledge, learning, notifyPhone, loading, missing, answer, discard, addKnowledge, updateKnowledge, removeKnowledge, setLearning, saveNotifyPhone } = useBotQuestions();
  const [phone, setPhone] = useState('');
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ question: '', answer: '' });
  const [addError, setAddError] = useState('');

  useEffect(() => { setPhone(notifyPhone || ''); }, [notifyPhone]);

  if (missing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261015a_bot_knowledge.sql</code> en Supabase para usar las preguntas del bot.</div>;
  }

  const open = questions.filter((q) => q.status === 'abierta');
  const closed = questions.filter((q) => q.status !== 'abierta').slice(0, 30);

  const add = async (e) => {
    e.preventDefault();
    const problem = checkKnowledge(newItem);
    if (problem) { setAddError(problem); return; }
    setAddError('');
    if (await addKnowledge(newItem)) { setNewItem({ question: '', answer: '' }); setAdding(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><HelpCircle size={20} /> Preguntas del bot</h2>
        <p className="text-xs text-seryi-text">Cuando el bot no sabe algo, no lo inventa: le dice al cliente que lo confirma y te pregunta. Tu respuesta le llega al cliente por WhatsApp y el bot la aprende para la próxima vez.</p>
      </div>

      {loading ? <p className="text-xs text-seryi-text">Cargando…</p> : (
        <>
          <section aria-label="Ajustes" className={card}>
            <label className="flex items-center gap-2 text-xs font-bold text-seryi-heading">
              <input type="checkbox" checked={learning} onChange={(e) => setLearning(e.target.checked)} />
              El bot consulta lo que no sabe y usa lo que ya aprendió
            </label>
            <form onSubmit={(e) => { e.preventDefault(); saveNotifyPhone(phone); }} className="space-y-1">
              <label htmlFor="bq-notify" className="block text-[11px] text-seryi-text">WhatsApp donde te llegan las preguntas (el mismo de los avisos de cotizaciones)</label>
              <div className="flex gap-2">
                <input id="bq-notify" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="573016867636" className={`${input} max-w-xs`} />
                <button type="submit" className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-4 py-2 rounded-xl">Guardar</button>
              </div>
              <p className="text-[11px] text-seryi-text">
                {notifyPhone
                  ? <>Desde ese WhatsApp responde escribiéndole al bot el código y tu respuesta: <strong className="text-seryi-heading">P-12 Sí, instalamos en Pereira</strong>. Con <strong className="text-seryi-heading">P-12!</strong> la respuesta es solo para ese cliente; con <strong className="text-seryi-heading">P-12 descartar</strong> no se le envía nada. Usa tu celular personal, no el WhatsApp del negocio.</>
                  : 'Sin número, las preguntas solo aparecen aquí y las respondes desde esta página.'}
              </p>
            </form>
          </section>

          <section aria-label="Por responder" className={card}>
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Por responder {open.length > 0 && <span className="text-fuchsia-300">({open.length})</span>}</h3>
            {open.length === 0 ? <p className="text-xs text-seryi-text">No hay preguntas pendientes.</p> : open.map((q) => <OpenQuestion key={q.id} q={q} onAnswer={answer} onDiscard={discard} />)}
          </section>

          <section aria-label="Lo que el bot ya sabe" className={card}>
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Lo que el bot ya sabe ({knowledge.filter((k) => k.active).length})</h3>
              {!adding && <button type="button" onClick={() => setAdding(true)} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-1.5 rounded-xl flex items-center gap-1"><Plus size={13} /> Agregar</button>}
            </div>
            {adding && (
              <form onSubmit={add} aria-label="Agregar conocimiento" className="border border-seryi-bg-light rounded-2xl p-3 space-y-2">
                <input aria-label="Pregunta nueva" value={newItem.question} maxLength={MAX_QUESTION} onChange={(e) => setNewItem({ ...newItem, question: e.target.value })} placeholder="¿Hacen envíos a otras ciudades?" className={input} />
                <textarea aria-label="Respuesta nueva" value={newItem.answer} maxLength={MAX_ANSWER} rows={2} onChange={(e) => setNewItem({ ...newItem, answer: e.target.value })} placeholder="Sí, enviamos a todo el país por transportadora." className={input} />
                <div className="flex gap-2">
                  <button type="submit" className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-3 py-1.5 rounded-xl">Guardar</button>
                  <button type="button" onClick={() => { setAdding(false); setAddError(''); }} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-1.5 rounded-xl">Cancelar</button>
                </div>
                {addError && <p role="alert" className="text-xs text-rose-300">{addError}</p>}
              </form>
            )}
            {knowledge.length === 0 ? <p className="text-xs text-seryi-text">Todavía nada. Lo que respondas a las preguntas del bot aparece aquí, y también puedes agregarlo a mano.</p>
              : knowledge.map((k) => <KnowledgeRow key={k.id} k={k} onUpdate={updateKnowledge} onRemove={removeKnowledge} />)}
            <p className="text-[10px] text-seryi-text">El bot lee hasta 40 respuestas activas (las más recientes).</p>
          </section>

          {closed.length > 0 && (
            <section aria-label="Historial" className={card}>
              <h3 className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading">Historial</h3>
              {closed.map((q) => {
                const d = deliveryInfo(q);
                return (
                  <div key={q.id} className="border-t border-seryi-bg-light pt-2 text-xs space-y-0.5">
                    <p className="text-[11px] text-seryi-text"><strong>{questionCode(q)}</strong> · {customerLabel(q)} · {ago(q.created_at)}</p>
                    <p className="text-seryi-heading">«{q.question}»</p>
                    {q.status === 'descartada' ? <p className="text-seryi-text">Descartada</p> : (
                      <>
                        <p className="text-seryi-text whitespace-pre-line">→ {q.answer}</p>
                        <p className={`text-[11px] ${d.tone}`}>{d.label}{q.delivery_status === 'error' && q.delivery_error ? `: ${q.delivery_error}` : ''}{q.learn ? '' : ' · solo para ese cliente'}</p>
                      </>
                    )}
                  </div>
                );
              })}
            </section>
          )}
        </>
      )}
    </div>
  );
}
